import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Canvas, useThree } from "@react-three/fiber";
import "./index.css";
import Navbar from "./components/Navbar";
import SiteFooter from "./components/SiteFooter";
import { Particles } from "./components/gl/particles";
import HeroSection from "./pages/HeroSection";
import About from "./pages/About";
import SkillsSection from "./pages/SkillsSection";
import CertificatesSection from "./pages/CertificatesSection";
import ContactSection from "./pages/ContactSection";
import ProjectsPage from "./pages/ProjectsPage";
import Test3DExperiencePage from "./pages/Test3DExperiencePage";
import SeoManager from "./components/SeoManager";
import { VercelAnalytics } from "./components/VercelAnalytics";
import { LocaleProvider } from "./context/LocaleContext";

// Mappa delle posizioni della camera per ogni pagina
const cameraPositions: Record<string, [number, number, number]> = {
  "/": [0.4, 0.2, 1.3],
  "/projects": [0.45, 0.25, 1.25],
  "/about": [0.6, 0.35, 1.0],
  "/skills": [0.3, 0.25, 1.35],
  "/certificates": [0.5, 0.4, 1.2],
  "/contact": [0.35, 0.15, 1.3],
  "/test-3d": [0.42, 0.28, 1.15],
};

// Componente che controlla la camera basato sulla route
const CameraController = () => {
  const { camera } = useThree();
  const location = useLocation();
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const targetPosition = cameraPositions[location.pathname] || cameraPositions["/"];
    const currentPosition = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
    const duration = 2.5; // 2.5 secondi di animazione
    let startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out-cubic)
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      camera.position.x =
        currentPosition.x + (targetPosition[0] - currentPosition.x) * easeProgress;
      camera.position.y =
        currentPosition.y + (targetPosition[1] - currentPosition.y) * easeProgress;
      camera.position.z =
        currentPosition.z + (targetPosition[2] - currentPosition.z) * easeProgress;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [location.pathname, camera]);

  return null;
};

// Assicuriamoci che l'elemento 'root' esista
const rootElement = document.getElementById("root");

const AppContent = () => {
  return (
    <div className="relative flex min-h-0 h-full w-full flex-1 flex-col overflow-hidden">
      {/* Sfondo con particelle 3D */}
      <div className="fixed inset-0 z-0 h-full w-full">
        <Canvas
          gl={{ antialias: true, alpha: true }}
          camera={{
            position: [0.5, 0.3, 1.2],
            fov: 50,
            near: 0.01,
            far: 300,
          }}
          style={{ background: "#000000" }}
        >
          <CameraController />
          <Particles
            speed={2}
            aperture={2.2}
            focus={0.75}
            size={256}
            noiseScale={1.0}
            noiseIntensity={0.5}
            timeScale={1}
            pointSize={2}
            opacity={0.8}
            planeScale={1.2}
          />
        </Canvas>
      </div>

      {/* Mesh radiale (layer fisso, no blur su contenuto scrollabile) */}
      <div
        className="pointer-events-none fixed inset-0 z-[1]"
        style={{
          background:
            "radial-gradient(ellipse 85% 55% at 15% 18%, rgba(124, 92, 255, 0.14), transparent 55%), radial-gradient(ellipse 70% 50% at 88% 72%, rgba(16, 185, 129, 0.11), transparent 50%), radial-gradient(ellipse 50% 40% at 50% 100%, rgba(251, 191, 36, 0.06), transparent 45%)",
        }}
        aria-hidden
      />

      {/* Leggibilità */}
      <div className="pointer-events-none fixed inset-0 z-[2] bg-[#050505]/45" aria-hidden />

      {/* Film grain */}
      <div className="portfolio-grain" aria-hidden />

      {/* Contenuto */}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
        <SeoManager />
        <Navbar />
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain"
          id="app-scroll"
        >
          {/* min-h-full: con contenuto corto il footer resta in fondo alla viewport; con contenuto lungo segue il flusso */}
          <div className="flex min-h-full flex-col gap-8 pb-6">
            <div className="flex w-full flex-1 flex-col">
              <Routes>
                <Route path="/" element={<HeroSection />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/experiences" element={<Navigate to="/projects" replace />} />
                <Route path="/about" element={<About />} />
                <Route path="/skills" element={<SkillsSection />} />
                <Route path="/certificates" element={<CertificatesSection />} />
                <Route path="/contact" element={<ContactSection />} />
                <Route path="/test-3d" element={<Test3DExperiencePage />} />
              </Routes>
            </div>
            <SiteFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <LocaleProvider>
        <AppContent />
      </LocaleProvider>
      <VercelAnalytics />
    </BrowserRouter>
  );
};

ReactDOM.createRoot(rootElement as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
